import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, User, Wallet, Download, Plus, CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

const SpreadScoreEditor = ({ loanId, currentScore, currentSpread, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [score, setScore] = useState(currentScore ?? '');
  const [spread, setSpread] = useState(currentSpread ?? '');

  const handleSave = async () => {
    try {
      const payload = {};
      if (score !== '' && score !== currentScore) payload.score = parseInt(score);
      if (spread !== '' && spread !== currentSpread) payload.credit_spread = parseFloat(spread);
      if (Object.keys(payload).length === 0) { setEditing(false); return; }
      const res = await api.put(`/loans/${loanId}/credit-score`, payload);
      toast.success(res.data.message || 'Credit info updated');
      setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  if (!editing) {
    return (
      <Button size="sm" variant="ghost" className="text-xs text-gray-500 h-7 px-2" onClick={() => setEditing(true)} data-testid={`edit-credit-${loanId}`}>
        <CreditCard className="w-3 h-3 mr-1" />Edit Score/Spread
      </Button>
    );
  }

  return (
    <div className="flex gap-2 items-center">
      <Input type="number" placeholder="Score" value={score} onChange={e => setScore(e.target.value)} className="h-7 w-20 text-xs" />
      <Input type="number" step="0.1" placeholder="Spread %" value={spread} onChange={e => setSpread(e.target.value)} className="h-7 w-20 text-xs" />
      <Button size="sm" onClick={handleSave} className="h-7 text-xs bg-[#1f1f1f] text-white px-2">Save</Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-xs px-2">Cancel</Button>
    </div>
  );
};

const LoansLookupPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [clientResult, setClientResult] = useState(null);
  const [multipleResults, setMultipleResults] = useState([]);
  const inputRef = useRef(null);
  const [showNewLoanModal, setShowNewLoanModal] = useState(false);
  const [loanProducts, setLoanProducts] = useState([]);
  const [loanClients, setLoanClients] = useState([]);
  const [loanForm, setLoanForm] = useState({ client_id: '', product_id: '', amount: '', term_months: '', purpose: '' });
  const [loanFormLoading, setLoanFormLoading] = useState(false);

  useEffect(() => {
    if (showNewLoanModal) {
      api.get('/loan-products').then(r => setLoanProducts((r.data || []).filter(p => p.is_active))).catch(() => {});
      api.get('/clients?status=active&limit=100').then(r => setLoanClients(r.data.clients || [])).catch(() => {});
    }
  }, [showNewLoanModal]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setClientResult(null);
    setMultipleResults([]);
    try {
      const res = await api.get(`/clients/lookup?q=${encodeURIComponent(query.trim())}`);
      if (res.data.match === 'single') {
        setClientResult(res.data);
      } else if (res.data.match === 'multiple') {
        setMultipleResults(res.data.clients);
      } else {
        toast.error('No client found. Try loan number, ID number, or name.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Search failed');
    }
    setSearching(false);
  };

  const selectClient = async (clientId) => {
    setSearching(true);
    try {
      const res = await api.get(`/clients/lookup?q=${clientId}`);
      if (res.data.match === 'single') {
        setClientResult(res.data);
        setMultipleResults([]);
      }
    } catch { toast.error('Failed to load client'); }
    setSearching(false);
  };

  const handleDownloadLoanStatement = async (loanId, loanNumber) => {
    try {
      const res = await api.get(`/loans/${loanId}/statement`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `LoanStatement_${loanNumber}_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Loan statement downloaded');
    } catch { toast.error('Failed to download loan statement'); }
  };

  const c = clientResult?.client;
  const loans = clientResult?.loans || [];

  return (
    <div className="space-y-6 fade-in" data-testid="loans-lookup-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Dashboard</span><span>/</span><span className="text-[#1f1f1f] font-medium">Loan Account Lookup</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">LOAN MANAGEMENT</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Loan Account Lookup</h1>
          <p className="text-sm text-gray-500 mt-1">Search by loan number, client name, or ID number</p>
        </div>
        <Button
          onClick={() => setShowNewLoanModal(true)}
          className="bg-[#1f1f1f] hover:bg-[#333333] text-white"
          data-testid="new-loan-application-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Loan Application
        </Button>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-3" data-testid="loans-search-form">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter loan number, client name, or ID number..."
            className="pl-12 py-6 text-base border-gray-200 focus:border-[#1f1f1f] focus:ring-[#1f1f1f]"
            data-testid="loans-lookup-search-input"
          />
        </div>
        <Button type="submit" disabled={searching} className="bg-[#1f1f1f] hover:bg-[#333333] px-8 py-6" data-testid="loans-lookup-search-btn">
          {searching ? 'Searching...' : 'Search'}
        </Button>
      </form>

      {/* Multiple Results */}
      {multipleResults.length > 0 && (
        <div className="bg-white border border-gray-200 p-4" data-testid="loans-multiple-results">
          <p className="text-sm font-medium text-[#1f1f1f] mb-3">Multiple clients found — select one:</p>
          <div className="space-y-2">
            {multipleResults.map(mc => (
              <button
                key={mc.id}
                onClick={() => selectClient(mc.client_number || mc.id_number)}
                className="w-full flex items-center gap-4 p-3 border border-gray-200 hover:bg-gray-50 text-left transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-[#1f1f1f]/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#1f1f1f]/50" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1f1f1f]">{mc.first_name} {mc.last_name}</p>
                  <p className="text-xs text-gray-500">{mc.client_number} | ID: {mc.id_number}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Client with Loans */}
      {c && (
        <div className="space-y-4" data-testid="loans-client-result">
          <div className="bg-[#1f1f1f] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <User className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-base font-bold">{c.first_name} {c.last_name}</h2>
                <p className="text-xs text-gray-400">{c.client_number} | {c.id_number} | {c.phone}</p>
              </div>
            </div>
            {c.credit_score && (
              <p className="text-xs text-gray-400">Credit Score: <span className="text-[#C5A059] font-bold">{c.credit_score}</span></p>
            )}
          </div>

          {loans.length === 0 ? (
            <div className="text-center py-8 bg-white border border-gray-200">
              <Wallet className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-3">No loans for this client</p>
              <Button size="sm" variant="outline" onClick={() => { setLoanForm(p => ({...p, client_id: c.id})); setShowNewLoanModal(true); }} data-testid="create-loan-for-client-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />New Loan Application
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {loans.map(loan => (
                <div key={loan.id} className="bg-white border border-gray-200 p-4" data-testid={`loan-lookup-card-${loan.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#1f1f1f]">{loan.loan_number || loan.id}</p>
                      <p className="text-xs text-gray-500">{loan.product_name || 'Loan'} | {loan.status}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#1f1f1f]">{formatCurrency(loan.principal_amount || loan.principal || loan.amount)}</p>
                      <p className="text-xs text-gray-400">Rate: {loan.interest_rate}% | Term: {loan.term_months}m</p>
                    </div>
                  </div>
                  <div className="flex gap-4 mt-2 text-xs">
                    {loan.account_credit_score != null && (
                      <span className="text-gray-500">Account Score: <span className="font-bold text-[#C5A059]">{loan.account_credit_score}</span></span>
                    )}
                    {loan.credit_spread != null && (
                      <span className="text-gray-500">Spread: <span className="font-bold text-[#C5A059]">{loan.credit_spread}%</span></span>
                    )}
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100 flex-wrap items-center">
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => handleDownloadLoanStatement(loan.id, loan.loan_number || loan.id)}>
                      <Download className="w-3.5 h-3.5 mr-1" />Statement
                    </Button>
                    {(user?.role === 'loan_officer' || user?.role === 'admin' || user?.role === 'manager') && (
                      <SpreadScoreEditor loanId={loan.id} currentScore={loan.account_credit_score} currentSpread={loan.credit_spread} onSaved={() => selectClient(c.id)} />
                    )}
                  </div>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => { setLoanForm(p => ({...p, client_id: c.id})); setShowNewLoanModal(true); }} data-testid="new-loan-for-client-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />New Loan Application
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!clientResult && multipleResults.length === 0 && !searching && (
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Search for a client to view their loan accounts</p>
        </div>
      )}

      {/* New Loan Application Modal */}
      <Dialog open={showNewLoanModal} onOpenChange={setShowNewLoanModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="new-loan-desc">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">New Loan Application</DialogTitle>
          </DialogHeader>
          <p id="new-loan-desc" className="sr-only">Apply for a new loan</p>
          <form onSubmit={async (e) => {
            e.preventDefault();
            setLoanFormLoading(true);
            try {
              await api.post('/loans', { ...loanForm, amount: parseFloat(loanForm.amount), term_months: parseInt(loanForm.term_months) });
              toast.success('Loan application submitted');
              setShowNewLoanModal(false);
              setLoanForm({ client_id: '', product_id: '', amount: '', term_months: '', purpose: '' });
            } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit loan'); }
            setLoanFormLoading(false);
          }} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Client *</Label>
                <Select value={loanForm.client_id} onValueChange={v => setLoanForm(p => ({...p, client_id: v}))}>
                  <SelectTrigger data-testid="lookup-loan-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{loanClients.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Loan Product *</Label>
                <Select value={loanForm.product_id} onValueChange={v => setLoanForm(p => ({...p, product_id: v}))}>
                  <SelectTrigger data-testid="lookup-loan-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                  <SelectContent>{loanProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.interest_rate}%)</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs uppercase tracking-wider text-gray-500">Amount (R) *</Label><Input type="number" value={loanForm.amount} onChange={e => setLoanForm(p => ({...p, amount: e.target.value}))} required data-testid="lookup-loan-amount" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-gray-500">Term (Months) *</Label><Input type="number" value={loanForm.term_months} onChange={e => setLoanForm(p => ({...p, term_months: e.target.value}))} required data-testid="lookup-loan-term" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-gray-500">Purpose</Label><Input value={loanForm.purpose} onChange={e => setLoanForm(p => ({...p, purpose: e.target.value}))} placeholder="e.g., Home improvement" data-testid="lookup-loan-purpose" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewLoanModal(false)}>Cancel</Button>
              <Button type="submit" disabled={loanFormLoading} className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="submit-new-loan-btn">{loanFormLoading ? 'Submitting...' : 'Submit Application'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoansLookupPage;